import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import useReveal from '../../hooks/useReveal';
import { contactApi } from '../../services/contactService';
import { toast } from 'react-toastify';
import type { ContactFormData } from '../../types';
import { Input } from '../../components/common/Input/Input';
import { Button } from '../../components/common/Button/Button';
import { InfoCard } from '../../components/InfoCard/InfoCard';
import { SuccessView } from '../../components/SuccessView/SuccessView';
import './Contact.css';


function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useReveal();

  const handleChange = (field: keyof ContactFormData) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await contactApi.submitMessage(form);
      setSent(true);
      toast.success(t('contact.form.success') || 'Thanks for submitting!');
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        message: '',
      });
    } catch {
      toast.error(t('errors.submitFailed') || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const handleReset = () => {
    setSent(false);
  };

  return (
    <main className="page-main">
      <section className="section contact-section">
        <div className="container contact-grid">
          <div className="contact-left reveal">
            <span className="contact-tagline">{t('contact.subtitle')}</span>
            <h1 className="contact-heading">{t('contact.title')}</h1>
            <p className="contact-desc">{t('contact.desc')}</p>
            
            <div className="contact-info-list">
              <InfoCard 
                title={t('contact.locationTitle')}
                description={t('contact.address')}
                href="https://www.google.com/maps/place/FCS+company/@24.7037057,46.6821346,17z"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                }
              />

              <InfoCard 
                title={t('contact.phoneTitle')}
                description={t('contact.phone')}
                href={`tel:${t('contact.phone')}`}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                }
              />

              <InfoCard 
                title={t('contact.whatsappTitle')}
                description={t('contact.phone')}
                href="https://wa.me/966595496463"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2m0 18.15a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24a8.2 8.2 0 0 1 8.24 8.24c0 4.54-3.7 8.24-8.24 8.24m4.52-6.16c-.25-.13-1.47-.72-1.69-.8-.23-.09-.4-.13-.56.12-.17.25-.64.8-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.38-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.13-.56-1.35-.77-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z" />
                  </svg>
                }
              />

              <InfoCard 
                title={t('contact.emailTitle')}
                description={t('contact.email')}
                href={`mailto:${t('contact.email')}`}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                }
              />
            </div>
          </div>
          
          <div className="contact-right reveal reveal-delay-2">
            <div className="contact-form-container">
              {sent ? (
                <SuccessView 
                  title={t('contact.form.successTitle')}
                  message={t('contact.form.success')}
                  submessage={t('contact.form.successSub')}
                  resetButtonText={t('contact.form.reset')}
                  onReset={handleReset}
                />
              ) : (
                <form onSubmit={handleSubmit} className="contact-form-modern">
                  <div className="contact-form-row">
                    <Input 
                      id="firstName"
                      label={t('contact.form.firstName')}
                      value={form.firstName}
                      onChange={handleChange('firstName')}
                      required
                      floating
                    />
                    <Input 
                      id="lastName"
                      label={t('contact.form.lastName')}
                      value={form.lastName}
                      onChange={handleChange('lastName')}
                      required
                      floating
                    />
                  </div>
                  
                  <Input 
                    id="email"
                    type="email"
                    label={t('contact.form.email')}
                    value={form.email}
                    onChange={handleChange('email')}
                    required
                    floating
                  />
                  
                  <Input 
                    id="message"
                    type="textarea"
                    label={t('contact.form.message')}
                    value={form.message}
                    onChange={handleChange('message')}
                    required
                    rows={5}
                    floating
                  />
                  
                  <Button type="submit" variant="modern" loading={submitting}>
                    {t('contact.form.send')}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact;
